/**
 * auth.ts — Cognito auth helpers
 *
 *
 * Sign-in uses `/oauth2/authorize`; invite sign-up uses `/signup` — separate
 * UserManager instances. {@link cognitoSigninCallbackFromRedirect} completes
 * the redirect with the manager that started it (PKCE state is per-manager).
 */

import type { User } from "oidc-client-ts";
import { getUserManager, getUserManagerForSignupHostedUi } from "./oidc";
import { useUserStore } from "@/stores/user";
import { useInsightsStore } from "@/stores/insights";
import { useAttendeeStore } from "@/stores/attendee";
import {
  COMPANY_COGNITO_INVITE_LOGOUT_ALERT_KEY,
  PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY,
  PENDING_SUBSCRIPTION_INVITE_SLUG_KEY,
} from "@/constants/subscriptionInvite";

export { useCognitoAuth } from "./cognito.config";
const COGNITO_COMPANY_OIDC_FLOW_SESSION_KEY = "cognito_company_oidc_flow";

function markCognitoCompanyOidcFlow(kind: "signup" | "signin"): void {
  try {
    sessionStorage.setItem(COGNITO_COMPANY_OIDC_FLOW_SESSION_KEY, kind);
  } catch {
    /* ignore */
  }
}

/**
 * Completes the OAuth redirect on `/auth/callback`. PKCE/state are stored on
 * the UserManager that called `signinRedirect`.
 */
export async function cognitoSigninCallbackFromRedirect(): Promise<User> {
  let preferSignup = false;
  try {
    preferSignup =
      sessionStorage.getItem(COGNITO_COMPANY_OIDC_FLOW_SESSION_KEY) ===
      "signup";
    sessionStorage.removeItem(COGNITO_COMPANY_OIDC_FLOW_SESSION_KEY);
  } catch {
    /* ignore */
  }

  const order = preferSignup
    ? [getUserManagerForSignupHostedUi, getUserManager]
    : [getUserManager, getUserManagerForSignupHostedUi];

  let lastErr: unknown;
  for (const getUm of order) {
    try {
      const user = await getUm().signinCallback();
      if (user?.access_token) return user;
    } catch (e) {
      lastErr = e;
      const msg = (e as Error)?.message ?? "";
      if (
        msg.includes("No matching state") ||
        msg.includes("No state in response")
      ) {
        continue;
      }
      throw e;
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("signinCallback failed for both UserManagers");
}

/** Redirect to Cognito Hosted UI (PKCE flow) */
export async function cognitoSignIn(): Promise<void> {
  const debug =
    process.env.NODE_ENV === "development" ||
    process.env.VUE_APP_COGNITO_DEBUG_OIDC === "true";
  if (debug) {
    console.info(
      "[Cognito][company] signinRedirect: starting PKCE sign-in (OIDC state + PKCE created by oidc-client-ts next)"
    );
  }
  markCognitoCompanyOidcFlow("signin");
  await getUserManager().signinRedirect();
}

export async function cognitoSignInFresh(): Promise<void> {
  const debug =
    process.env.NODE_ENV === "development" ||
    process.env.VUE_APP_COGNITO_DEBUG_OIDC === "true";
  if (debug) {
    console.info(
      "[Cognito][company] cognitoSignInFresh: It called the cognitoClearAllLocalSessions and remove session"
    );
  }
  await cognitoClearAllLocalSessions();
  // Clear OIDC PKCE residuals from sessionStorage so stale state tokens
  // cannot cause a "state mismatch" error on the next signinRedirect.
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (
        k &&
        (k.startsWith("oidc.") ||
          k.includes("oidc") ||
          k.toLowerCase().includes("pkce"))
      ) {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID as string;
  const logoutUri = process.env.VUE_APP_COGNITO_REDIRECT_SIGN_OUT as string;
  const cognitoDomain = `https://${process.env.VUE_APP_COGNITO_DOMAIN}`;
  const logoutUrl =
    `${cognitoDomain}/logout?client_id=${clientId}` +
    `&logout_uri=${encodeURIComponent(logoutUri)}`;
  globalThis.location.href = logoutUrl;
}

/**
 * Expert app → company app: wipe stale company Pinia/OIDC, then OAuth authorize
 * with login_hint so SSO can reuse the existing Cognito session — smooth switch.
 */
export async function cognitoSignInAfterExpertHandoff(
  userEmail: string
): Promise<void> {
  const email = userEmail.trim();
  const debug =
    process.env.NODE_ENV === "development" ||
    process.env.VUE_APP_COGNITO_DEBUG_OIDC === "true";
  if (debug) {
    console.info("[Cognito][company] signinRedirect after expert handoff", {
      hasEmail: Boolean(email),
      path: "cognito_sso",
    });
  }
  markCognitoCompanyOidcFlow("signin");
  if (!email) {
    await getUserManager().signinRedirect();
    return;
  }
  await getUserManager().signinRedirect({
    extraQueryParams: { login_hint: email },
  });
}

export type CognitoSignUpRedirectOptions = {
  /** OIDC login_hint; Cognito Managed Login may not pre-fill the signup field. */
  loginHint?: string;
};

/**
 * Subscription-invite flow: open Cognito **managed login sign-up** (`/signup`).
 * Callback uses {@link cognitoSigninCallbackFromRedirect}.
 */
export async function cognitoSignUpRedirect(
  options?: CognitoSignUpRedirectOptions
): Promise<void> {
  const debug =
    process.env.NODE_ENV === "development" ||
    process.env.VUE_APP_COGNITO_DEBUG_OIDC === "true";
  if (debug) {
    console.info(
      "[Cognito][company] signinRedirect: PKCE redirect to Hosted UI /signup",
      { hasLoginHint: Boolean(options?.loginHint?.trim()) }
    );
  }
  const hint = options?.loginHint?.trim();
  markCognitoCompanyOidcFlow("signup");
  await getUserManagerForSignupHostedUi().signinRedirect(
    hint ? { login_hint: hint } : undefined
  );
}

/** Clear tokens/state for both sign-in and sign-up Hosted UI flows (separate UserManagers). */
export async function cognitoClearAllLocalSessions(): Promise<void> {
  try {
    await getUserManager().removeUser();
  } catch {
    /* ignore */
  }
  try {
    await getUserManagerForSignupHostedUi().removeUser();
  } catch {
    /* ignore */
  }
}

/**
 * Cognito Hosted UI logout URL (same as {@link cognitoSignOut} navigation target).
 */
export function cognitoHostedLogoutUrl(): string {
  const clientId = process.env.VUE_APP_COGNITO_CLIENT_ID as string;
  const logoutUri = process.env.VUE_APP_COGNITO_REDIRECT_SIGN_OUT as string;
  const cognitoDomain = `https://${process.env.VUE_APP_COGNITO_DOMAIN}`;
  return `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
    logoutUri
  )}`;
}

/** Tab keys used for Cognito invite/OIDC flows — cleared on logout (not `sessionStorage.clear()` so disabled-account alert can survive Apollo 401 → logout). */
function clearCompanyCognitoFlowSessionStorage(): void {
  try {
    sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);
    sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
    sessionStorage.removeItem(COMPANY_COGNITO_INVITE_LOGOUT_ALERT_KEY);
    sessionStorage.removeItem(COGNITO_COMPANY_OIDC_FLOW_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

function clearCompanyPiniaSession(): void {
  try {
    useUserStore().clearStore();
    useInsightsStore().clearStore();
    useAttendeeStore().clearStore();
  } catch {
    /* Pinia may be unavailable outside app context */
  }
}

/**
 * Expert app → company app handoff: wipe stale company Pinia + OIDC + flow session keys.
 * Mirrors clearExpertAppSessionForCompanyHandoff on the expert side.
 */
export async function clearCompanyAppSessionForExpertHandoff(): Promise<void> {
  clearCompanyPiniaSession();
  await cognitoClearAllLocalSessions();
  clearCompanyCognitoFlowSessionStorage();
}

export async function clearCognitoSessionLocalOnly(): Promise<void> {
  await clearCompanyAppSessionForExpertHandoff();
}

/** Redirect to Cognito logout: Pinia + OIDC + Cognito flow session keys, then Hosted UI `/logout`. */
export async function cognitoSignOut(): Promise<void> {
  await clearCognitoSessionLocalOnly();
  globalThis.location.href = cognitoHostedLogoutUrl();
}

/**
 * Return the current user's access token, or null if not signed in or token is expired.
 * Checks `user.expired` (from oidc-client-ts) to avoid returning stale tokens.
 */
export async function getCognitoAccessToken(): Promise<string | null> {
  for (const getUm of [getUserManager, getUserManagerForSignupHostedUi]) {
    try {
      const user = await getUm().getUser();
      if (user?.access_token && !user.expired) return user.access_token;
    } catch {
      /* try next */
    }
  }
  return null;
}
